import { Controller } from "@hotwired/stimulus"

// Single-slot drag-and-drop uploader for DpkProfile attachments (one_sheet, press_sheet).
// Each instance manages one slot. Empty state shows a drop zone; populated state shows
// a thumb + filename + replace/delete buttons. The server compresses to 4MB JPEG.
export default class extends Controller {
  static targets = ["dropZone", "populatedState", "thumb", "filename", "filesize", "fileInput", "progress"]
  static values = {
    uploadUrl: String,
    deleteUrl: String,
    slot: String,
    label: String
  }

  connect() {
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  dragOver(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.add("border-[var(--color-mc-accent-terracotta)]", "bg-[var(--color-mc-accent-terracotta)]/5")
    }
  }

  dragLeave(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.remove("border-[var(--color-mc-accent-terracotta)]", "bg-[var(--color-mc-accent-terracotta)]/5")
    }
  }

  drop(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.remove("border-[var(--color-mc-accent-terracotta)]", "bg-[var(--color-mc-accent-terracotta)]/5")
    }
    const file = event.dataTransfer.files[0]
    if (file) this.uploadFile(file)
  }

  openFilePicker(event) {
    if (event) event.preventDefault()
    this.fileInputTarget.click()
  }

  fileSelected(event) {
    const file = event.target.files[0]
    if (file) this.uploadFile(file)
    event.target.value = ""
  }

  async deleteAttachment(event) {
    event.preventDefault()
    const confirmFn = window.appConfirm || ((msg) => Promise.resolve(confirm(msg)))
    if (!(await confirmFn(`Delete the ${this.labelValue}?`))) return

    try {
      const response = await fetch(`${this.deleteUrlValue}?slot=${encodeURIComponent(this.slotValue)}`, {
        method: "DELETE",
        headers: { "X-CSRF-Token": this.csrfToken, "Accept": "application/json" }
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok || !data.success) {
        const msg = data.error || `Server error (${response.status})`
        if (window.showToast) window.showToast("error", `Failed to delete ${this.labelValue}: ${msg}`)
        return
      }
      this.renderEmptyState()
      if (window.showToast) window.showToast("success", `${this.labelValue} deleted.`)
    } catch (err) {
      if (window.showToast) window.showToast("error", `Failed to delete ${this.labelValue}: ${err.message}`)
    }
  }

  async uploadFile(file) {
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"]
    const maxSize = 4 * 1024 * 1024

    if (!allowedTypes.includes(file.type)) {
      if (file.type === "application/pdf") {
        if (window.showToast) window.showToast("error", "Only image files are supported (JPG, PNG, GIF, WebP). PDFs are not supported.")
      } else {
        if (window.showToast) window.showToast("error", "Only image files are supported (JPG, PNG, GIF, WebP).")
      }
      return
    }

    if (file.size > maxSize) {
      const mb = (file.size / 1024 / 1024).toFixed(1)
      if (window.showToast) window.showToast("error", `File is ${mb} MB. Maximum size is 4 MB. Please compress your image and try again.`)
      return
    }

    const isReplace = this.hasPopulatedStateTarget && !this.populatedStateTarget.classList.contains("hidden")
    this.showProgress(file, isReplace)

    const formData = new FormData()
    formData.append("slot", this.slotValue)
    formData.append("file", file)

    try {
      const response = await fetch(this.uploadUrlValue, {
        method: "PATCH",
        headers: { "X-CSRF-Token": this.csrfToken, "Accept": "application/json" },
        body: formData
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok || !data.success) {
        const msg = data.error || `Server error (${response.status})`
        if (window.showToast) window.showToast("error", msg)
        this.hideProgress()
        return
      }
      this.renderPopulatedState(data)
      if (window.showToast) window.showToast("success", `${this.labelValue} uploaded.`)
    } catch (err) {
      if (window.showToast) window.showToast("error", `${this.labelValue} upload failed: ${err.message}`)
      this.hideProgress()
    }
  }

  showProgress(file, isReplace) {
    if (this.hasProgressTarget) {
      this.progressTarget.classList.remove("hidden")
      this.progressTarget.textContent = `Uploading ${file.name}...`
    }
    if (isReplace && this.hasThumbTarget) {
      this.thumbTarget.classList.add("opacity-50", "transition-opacity")
    }
  }

  hideProgress() {
    if (this.hasProgressTarget) {
      this.progressTarget.classList.add("hidden")
    }
    if (this.hasThumbTarget) {
      this.thumbTarget.classList.remove("opacity-50")
    }
  }

  renderEmptyState() {
    if (this.hasPopulatedStateTarget) this.populatedStateTarget.classList.add("hidden")
    if (this.hasDropZoneTarget) this.dropZoneTarget.classList.remove("hidden")
    this.hideProgress()
  }

  renderPopulatedState(data) {
    if (this.hasDropZoneTarget) this.dropZoneTarget.classList.add("hidden")
    if (this.hasPopulatedStateTarget) this.populatedStateTarget.classList.remove("hidden")
    if (this.hasThumbTarget) {
      this.thumbTarget.classList.remove("opacity-50")
      this.thumbTarget.src = data.thumb_url
    }
    if (this.hasFilenameTarget) this.filenameTarget.textContent = data.filename
    if (this.hasFilesizeTarget) {
      const mb = (data.size / 1024 / 1024).toFixed(2)
      this.filesizeTarget.textContent = `${mb} MB`
    }
    this.hideProgress()
  }
}
